import type { Editor } from "@tiptap/core";
import {
  getPrintTableChoiceSlotForIndex,
  getTableVariableColumns,
} from "../../core/config";
import type {
  DirectiveChoiceSlot,
  TableVariableColumn,
} from "../../core/config";
import { buildDirectiveRaw } from "../../core/parseRaw";
import type { DirectiveBuilderStep } from "../storage/session";
import { blurUpdateAndFocusFloatingDirectiveChoice } from "../../builder/interaction/focusChoice";
import { syncFloatingBuilderChoiceStorage } from "../storage/floatingStorage";

const PRINT_TABLE_DIRECTIVE_KEY = "print(table)";

export const isPrintTable = (directiveKey: string): boolean =>
  directiveKey === PRINT_TABLE_DIRECTIVE_KEY;

export interface DirectiveBuilderState {
  step: DirectiveBuilderStep;
  directiveKey: string;
  variableName: string;
  choices: string[];
  choiceIndex: number;
  selectedIndex: number;
}

export interface DirectiveSuggestionStateRef {
  builder: DirectiveBuilderState;

  listFilterText: string;
  update: () => void;

  directiveTokenFrom: number | null;

  syncDocumentDirectiveRaw: (raw: string) => void;

  blurDomBeforeFloatingChoiceFocus?: () => void;

  floatingUiContainer?: HTMLElement | null;

  floatingChoiceFieldEl?: HTMLElement | null;
}

export interface InsertAndCloseFn {
  (raw: string): void;
}

export const getPrintTableChoiceSlot = (
  tableColumns: TableVariableColumn[],
  choices: string[],
  choiceIndex: number,
): DirectiveChoiceSlot =>
  getPrintTableChoiceSlotForIndex(tableColumns, choices, choiceIndex);

export const handlePrintTableChoiceSubmit = (
  editor: Editor,
  builder: DirectiveBuilderState,
  value: string,
  stateRef: DirectiveSuggestionStateRef,
  tableColumns: TableVariableColumn[],
  insertAndClose: InsertAndCloseFn,
): void => {
  if (value.trim() === "") {
    insertAndClose(
      buildDirectiveRaw(
        builder.directiveKey,
        builder.variableName,
        builder.choices,
      ),
    );
    return;
  }
  const slotAtIndex = getPrintTableChoiceSlotForIndex(
    tableColumns,
    builder.choices,
    builder.choiceIndex,
  );
  const opts = slotAtIndex.options;
  if (opts != null && !opts.some((o) => o.value === value.trim())) {
    return;
  }
  const newChoices = (() => {
    const next = [...builder.choices];
    next[builder.choiceIndex] = value.trim();
    return next;
  })();
  if (newChoices.length >= tableColumns.length) {
    insertAndClose(
      buildDirectiveRaw(builder.directiveKey, builder.variableName, newChoices),
    );
    return;
  }
  stateRef.builder = {
    ...builder,
    choices: newChoices,
    choiceIndex: newChoices.length,
  };
  syncFloatingBuilderChoiceStorage(editor, stateRef);
  stateRef.syncDocumentDirectiveRaw(
    buildDirectiveRaw(builder.directiveKey, builder.variableName, newChoices),
  );
  blurUpdateAndFocusFloatingDirectiveChoice(stateRef);
};

export { getTableVariableColumns };
